<template>
  <div class="create-table-page">
    <div class="create-table-container">
      <div class="page-header">
        <h1 class="page-title">Create New App Deploy Table</h1>
        <p class="description">
          Configure a new Azure storage table for application deployment
          management
        </p>
      </div>

      <!-- Message display section -->
      <div v-if="message.text" :class="['message', message.type]">
        <span class="message-icon">{{
          message.type === "success" ? "✓" : "⚠"
        }}</span>
        <span class="message-text">{{ message.text }}</span>
      </div>

      <!-- Configuration Card -->
      <div class="config-card">
        <h2 class="card-title">Table Configuration</h2>

        <div class="form-section">
          <div class="form-row">
            <div class="form-group">
              <label for="clientCode">
                Client Code
                <span class="required">*</span>
              </label>
              <input
                id="clientCode"
                v-model="clientCode"
                @input="handleClientCodeInput"
                placeholder="ABC"
                class="input-element"
                :class="{ 'input-error': clientCodeError }"
                maxlength="3"
              />
              <span class="input-hint">Must be exactly 3 letters</span>
              <span v-if="clientCodeError" class="error-text">
                <span class="error-icon">!</span>
                Client code must be exactly 3 letters
              </span>
            </div>

            <div class="form-group">
              <label for="tableType">
                Table Type
                <span class="required">*</span>
              </label>
              <select
                id="tableType"
                v-model="selectedTableType"
                class="select-element"
              >
                <option value="" disabled>Select table type...</option>
                <option value="WorkstationApps">Workstation Apps</option>
                <option value="RemotePCs">Remote PCs</option>
                <option value="ServerApps">Server Apps</option>
              </select>
              <span class="input-hint">Choose the deployment category</span>
            </div>
          </div>

          <div
            v-if="clientCode && selectedTableType && !clientCodeError"
            class="table-name-preview"
          >
            <div class="preview-label">Table Name Preview</div>
            <div class="preview-value">{{ previewTableName }}</div>
          </div>
        </div>
      </div>

      <!-- Actions Card -->
      <div class="actions-card">
        <div class="action-group primary-actions">
          <button
            @click="createNewClientTable"
            class="primary-button"
            :disabled="!isFormValid"
          >
            <span class="button-icon">+</span>
            Create Table
          </button>
          <button
            @click="updateNewClientTable"
            class="primary-button add-apps"
            :disabled="!newTableName"
          >
            <span class="button-icon">↓</span>
            Add Applications
          </button>
        </div>
        <div class="action-group secondary-actions">
          <button @click="returnToTables" class="secondary-button">
            <span class="button-icon">←</span>
            Return to Tables
          </button>
        </div>
      </div>

      <!-- Application Selection Section -->
      <div v-if="selectedTable && tableContents.length" class="selection-card">
        <div class="card-header">
          <div>
            <h2 class="card-title">Select Applications</h2>
            <p class="card-description">
              Choose applications from the template list to add to your new
              table
            </p>
          </div>
          <div v-if="selectedApplications.length" class="selection-badge">
            {{ selectedApplications.length }} selected
          </div>
        </div>

        <div class="table-scroll-wrapper">
          <!-- Top scrollbar -->
          <div
            class="top-scrollbar"
            ref="topScrollbar"
            @scroll="syncScrollFromTop"
          >
            <div class="scrollbar-content"></div>
          </div>

          <!-- Main table container -->
          <div
            class="table-container"
            ref="tableContainer"
            @scroll="syncScrollFromTable"
          >
            <table>
              <thead>
                <tr>
                  <th class="checkbox-column sticky-column">
                    <input
                      type="checkbox"
                      @change="toggleAllApplications"
                      :checked="
                        selectedApplications.length === tableContents.length
                      "
                    />
                  </th>
                  <th
                    v-for="header in tableHeaders"
                    :key="header"
                    @click="sortTable(header)"
                  >
                    {{ header }}
                    <span v-if="sortColumn === header" class="sort-indicator">
                      {{ sortOrder === "asc" ? "↑" : "↓" }}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in sortedTableContents"
                  :key="row.PartitionKey + '-' + row.rowKey"
                  :class="{ 'row-selected': isRowSelected(row) }"
                >
                  <td class="checkbox-column sticky-column">
                    <input
                      type="checkbox"
                      :value="row"
                      v-model="selectedApplications"
                    />
                  </td>
                  <td
                    v-for="header in tableHeaders"
                    :key="header"
                    class="table-cell"
                  >
                    {{ row[header] }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { msalInstance } from "@/auth.js";
import {
  getTableContents,
  createClientTable,
  updateClientTable,
} from "@/azure-devops-api.js";
import { mapState } from "vuex";

export default {
  components: {},
  data() {
    return {
      selectedTable: "GENWorkstationApps",
      tableContents: [],
      tableHeaders: [],
      sortColumn: "",
      sortOrder: "asc",
      clientCode: "",
      selectedTableType: "",
      selectedApplications: [],
      newTableName: "",
      clientCodeError: false,
      message: {
        text: "",
        type: "",
      },
      isScrollSyncing: false,
    };
  },
  computed: {
    ...mapState(["isLoggedIn"]),
    previewTableName() {
      if (!this.clientCode || !this.selectedTableType) return "";
      return `${this.clientCode}${this.selectedTableType}`;
    },
    isFormValid() {
      return (
        this.clientCode.length === 3 &&
        this.selectedTableType &&
        !this.clientCodeError
      );
    },
    sortedTableContents() {
      if (!this.sortColumn) return this.tableContents;
      return this.tableContents.slice().sort((a, b) => {
        const aValue = a[this.sortColumn];
        const bValue = b[this.sortColumn];
        if (aValue < bValue) return this.sortOrder === "asc" ? -1 : 1;
        if (aValue > bValue) return this.sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    },
  },
  beforeRouteEnter(to, from, next) {
    if (!msalInstance.getAllAccounts().length) {
      next("/login");
    } else {
      next();
    }
  },
  async created() {
    try {
      const contents = await getTableContents(this.selectedTable);
      this.tableContents = contents;
      const headers = Object.keys(contents[0]);
      this.tableHeaders = [
        "AppName",
        "Version",
        ...headers.filter((h) => h !== "AppName" && h !== "Version"),
      ];
    } catch (error) {
      console.error("Error fetching table contents:", error);
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.updateScrollbarWidth();
    });
  },
  methods: {
    handleClientCodeInput(event) {
      let value = event.target.value.toUpperCase();
      value = value.replace(/[^A-Z]/g, "");
      if (value.length > 3) {
        value = value.substring(0, 3);
      }
      this.clientCode = value;
      this.clientCodeError =
        this.clientCode.length > 0 && this.clientCode.length !== 3;
    },
    updateScrollbarWidth() {
      if (this.$refs.tableContainer && this.$refs.topScrollbar) {
        const table = this.$refs.tableContainer.querySelector("table");
        if (table) {
          const scrollbarContent =
            this.$refs.topScrollbar.querySelector(".scrollbar-content");
          scrollbarContent.style.width = table.scrollWidth + "px";
        }
      }
    },
    syncScrollFromTop(event) {
      if (this.isScrollSyncing) return;
      this.isScrollSyncing = true;
      if (this.$refs.tableContainer) {
        this.$refs.tableContainer.scrollLeft = event.target.scrollLeft;
      }
      this.$nextTick(() => {
        this.isScrollSyncing = false;
      });
    },
    syncScrollFromTable(event) {
      if (this.isScrollSyncing) return;
      this.isScrollSyncing = true;
      if (this.$refs.topScrollbar) {
        this.$refs.topScrollbar.scrollLeft = event.target.scrollLeft;
      }
      this.$nextTick(() => {
        this.isScrollSyncing = false;
      });
    },
    async createNewClientTable() {
      if (this.clientCode.length !== 3) {
        this.message.text = "Client code must be exactly 3 letters.";
        this.message.type = "error";
        return;
      }
      if (!this.selectedTableType) {
        this.message.text = "Please select a table type.";
        this.message.type = "error";
        return;
      }

      this.newTableName = `${this.clientCode}${this.selectedTableType}`;

      try {
        const response = await createClientTable(this.newTableName);
        console.log("Response:", response);

        if (response.status === 201) {
          this.message.text = `Table '${this.newTableName}' created successfully!`;
          this.message.type = "success";
        } else if (response.status === 409) {
          this.message.text = `Table '${this.newTableName}' already exists.`;
          this.message.type = "error";
        } else {
          this.message.text = `Unexpected error: ${response.message}`;
          this.message.type = "error";
        }
      } catch (error) {
        console.error("Error creating new client table:", error);
        this.message.text = `Error creating table: ${JSON.stringify(error)}`;
        this.message.type = "error";
      }
    },
    async updateNewClientTable() {
      if (!this.selectedApplications.length) {
        this.message.text = "Please select at least one application.";
        this.message.type = "error";
        return;
      }
      if (!this.newTableName) {
        this.message.text = "No table to update. Please create a table first.";
        this.message.type = "error";
        return;
      }

      try {
        for (let app of this.selectedApplications) {
          const nextRowKey = await this.getNextRowKeyForSelectedTable();
          const updatedApp = { ...app, rowKey: nextRowKey };

          const response = await updateClientTable(
            this.newTableName,
            updatedApp
          );
          if (response.status !== 200) {
            this.message.text = `Error updating table: ${response.message}`;
            this.message.type = "error";
            return;
          }
        }
        this.message.text = `Table '${this.newTableName}' updated successfully with ${this.selectedApplications.length} application(s).`;
        this.message.type = "success";
        this.selectedApplications = [];

        // Update scrollbar width after content changes
        this.$nextTick(() => {
          this.updateScrollbarWidth();
        });
      } catch (error) {
        console.error("Error updating new client table:", error);
        this.message.text = `Error updating table: ${JSON.stringify(error)}`;
        this.message.type = "error";
      }
    },
    async getNextRowKeyForSelectedTable() {
      try {
        const contents = await getTableContents(this.newTableName);
        console.log("Fetched table contents for selected table:", contents);

        if (contents.length === 0) {
          return "0001";
        }

        const rowKeys = contents.map((item) => {
          if (item.rowKey && typeof item.rowKey === "string") {
            const parsedKey = parseInt(item.rowKey, 10);
            if (isNaN(parsedKey)) {
              console.error(
                "Invalid rowKey encountered, non-numeric:",
                item.rowKey
              );
              return 0;
            }
            return parsedKey;
          } else {
            console.error("rowKey missing or not a string:", item);
            return 0;
          }
        });

        const maxRowKey = Math.max(...rowKeys);
        const nextRowKey = (maxRowKey + 1).toString().padStart(4, "0");
        console.log("Calculated next rowKey for selected table:", nextRowKey);
        return nextRowKey;
      } catch (error) {
        console.error("Error fetching rowKey for selected table:", error);
        return "0001";
      }
    },
    sortTable(column) {
      if (this.sortColumn === column) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortColumn = column;
        this.sortOrder = "asc";
      }
    },
    toggleAllApplications(event) {
      if (event.target.checked) {
        this.selectedApplications = [...this.tableContents];
      } else {
        this.selectedApplications = [];
      }
    },
    isRowSelected(row) {
      return this.selectedApplications.some(
        (app) =>
          app.PartitionKey === row.PartitionKey && app.rowKey === row.rowKey
      );
    },
    returnToTables() {
      this.$router.push("/storage-tables-list");
    },
  },
  watch: {
    tableContents() {
      this.$nextTick(() => {
        this.updateScrollbarWidth();
      });
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap");

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI",
    sans-serif;
  line-height: 1.6;
  background: #f5f7fa;
}

.container {
  width: 100%;
  margin: 0 auto;
}

.header {
  background: #0e223b;
  color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.top-bar {
  background: #ffffff;
  padding: 20px 0;
  text-align: center;
}

.top-bar .logo {
  text-align: left;
}

.top-bar .logo img {
  width: 150px;
  margin-left: 20px;
}

.nav-bar {
  background: #0e223b;
  padding: 10px 0;
}

.nav-bar a {
  color: #fff;
  margin: 0 15px;
  text-decoration: none;
  transition: color 0.3s;
}

.nav-bar a:hover {
  color: #38b35f;
}

.create-table-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 2rem;
}

.page-header {
  text-align: center;
  margin-bottom: 3rem;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 0.75rem;
  font-family: "Inter", sans-serif;
}

.description {
  font-size: 1.125rem;
  color: #6b7280;
  font-weight: 400;
}

.config-card,
.actions-card,
.selection-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 2.5rem;
  margin-bottom: 2rem;
}

.card-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 0.75rem;
}

.card-description {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
}

.selection-badge {
  background: #38b35f;
  color: white;
  padding: 0.625rem 1.25rem;
  border-radius: 24px;
  font-size: 0.9375rem;
  font-weight: 600;
}

.form-section {
  margin-top: 2rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-bottom: 2rem;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.75rem;
  font-family: "Inter", sans-serif;
}

.required {
  color: #dc3545;
  margin-left: 2px;
}

.input-element,
.select-element {
  width: 100%;
  padding: 1rem 1.25rem;
  font-size: 1.0625rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-family: "Inter", sans-serif;
  transition: all 0.2s ease;
  background: white;
}

.input-element:focus,
.select-element:focus {
  outline: none;
  border-color: #38b35f;
  box-shadow: 0 0 0 4px rgba(56, 179, 95, 0.1);
}

.input-element.input-error {
  border-color: #dc3545;
}

.input-element.input-error:focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 4px rgba(220, 53, 69, 0.1);
}

.input-hint {
  font-size: 0.875rem;
  color: #9ca3af;
  margin-top: 0.5rem;
}

.error-text {
  font-size: 0.875rem;
  color: #dc3545;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-weight: 500;
}

.error-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background: #dc3545;
  color: white;
  border-radius: 50%;
  font-size: 0.75rem;
  font-weight: bold;
}

.select-element {
  cursor: pointer;
  appearance: none;
  /* cSpell:ignore Csvg, Cpath */
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  padding-right: 3rem;
}

.table-name-preview {
  padding: 1.75rem 2rem;
  background: linear-gradient(135deg, #e8f5e9 0%, #f1f8f4 100%);
  border: 2px solid #38b35f;
  border-radius: 10px;
  margin-top: 2rem;
}

.preview-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #2e7d32;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.75rem;
}

.preview-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1b5e20;
  font-family: "Courier New", monospace;
  letter-spacing: 0.5px;
}

.actions-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.action-group {
  display: flex;
  gap: 1rem;
}

.primary-actions {
  flex: 1;
}

@media (max-width: 768px) {
  .actions-card {
    flex-direction: column;
  }

  .action-group {
    width: 100%;
  }

  .action-group button {
    flex: 1;
  }
}

.primary-button,
.secondary-button {
  font-family: "Inter", sans-serif;
  padding: 1rem 2rem;
  font-size: 1.0625rem;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  white-space: nowrap;
}

.button-icon {
  font-size: 1.25rem;
  font-weight: bold;
}

.primary-button {
  background: #38b35f;
  color: white;
  box-shadow: 0 2px 4px rgba(56, 179, 95, 0.3);
}

.primary-button:hover:not(:disabled) {
  background: #2e9d51;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(56, 179, 95, 0.3);
}

.primary-button:active:not(:disabled) {
  transform: translateY(0);
}

.primary-button:disabled {
  background: #d1d5db;
  cursor: not-allowed;
  box-shadow: none;
  opacity: 0.6;
}

.primary-button.add-apps {
  background: #1c7ed6;
}

.primary-button.add-apps:hover:not(:disabled) {
  background: #1864ab;
}

.secondary-button {
  background: white;
  color: #6b7280;
  border: 2px solid #e5e7eb;
}

.secondary-button:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.message {
  margin-bottom: 2rem;
  padding: 1.25rem 1.5rem;
  border-radius: 10px;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-weight: bold;
  flex-shrink: 0;
  font-size: 1.125rem;
}

.message.success {
  background-color: #d1f4e0;
  color: #0d5c2f;
  border-left: 4px solid #38b35f;
}

.message.success .message-icon {
  background: #38b35f;
  color: white;
}

.message.error {
  background-color: #fee;
  color: #991b1b;
  border-left: 4px solid #dc3545;
}

.message.error .message-icon {
  background: #dc3545;
  color: white;
}

.table-scroll-wrapper {
  position: relative;
}

.top-scrollbar {
  overflow-x: auto;
  overflow-y: hidden;
  height: 16px;
  margin-bottom: 1px;
  border: 1px solid #e5e7eb;
  border-radius: 8px 8px 0 0;
  background: #f9fafb;
}

.scrollbar-content {
  height: 1px;
  pointer-events: none;
}

/* Custom scrollbar styling for top scrollbar */
.top-scrollbar::-webkit-scrollbar {
  height: 14px;
}

.top-scrollbar::-webkit-scrollbar-track {
  background: #f1f3f5;
  border-radius: 8px 8px 0 0;
}

.top-scrollbar::-webkit-scrollbar-thumb {
  background: #38b35f;
  border-radius: 6px;
  border: 2px solid #f1f3f5;
}

.top-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #2e9d51;
}

.table-container {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  border: 1px solid #e5e7eb;
  border-top: none;
  border-radius: 0 0 8px 8px;
  position: relative;
}

/* Custom scrollbar styling for table container */
.table-container::-webkit-scrollbar {
  height: 14px;
}

.table-container::-webkit-scrollbar-track {
  background: #f1f3f5;
  border-radius: 0 0 8px 8px;
}

.table-container::-webkit-scrollbar-thumb {
  background: #38b35f;
  border-radius: 6px;
  border: 2px solid #f1f3f5;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: #2e9d51;
}

table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1200px;
}

th,
td {
  padding: 1rem 1.25rem;
  text-align: left;
  white-space: nowrap;
  font-size: 0.9375rem;
}

th {
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  user-select: none;
  border-bottom: 2px solid #e5e7eb;
  position: relative;
}

th:hover {
  background: #f3f4f6;
}

.sort-indicator {
  margin-left: 0.375rem;
  color: #38b35f;
  font-weight: bold;
}

.checkbox-column {
  width: 60px;
  text-align: center;
}

.sticky-column {
  position: sticky;
  left: 0;
  z-index: 10;
  background: #f9fafb;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
}

tbody tr .sticky-column {
  background: white;
}

tbody tr:hover .sticky-column {
  background: #f9fafb;
}

tbody tr.row-selected .sticky-column {
  background: #f0fdf4;
}

tbody tr {
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.15s ease;
}

tbody tr:hover {
  background: #f9fafb;
}

tbody tr.row-selected {
  background: #f0fdf4;
}

.table-cell {
  color: #4b5563;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
}

input[type="checkbox"] {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #38b35f;
}
</style>
